import { message } from 'antd';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { type AdminPermission, hasAdminPermission } from '@/auth/permissions';
import { ACCOUNT_APP_ID } from '@/config/domains';
import { accountService } from '@/services/accountService';
import { adminAccessApi } from '@/services/adminAccessApi';
import { authApi } from '@/services/authApi';
import type { AuthType, GetAdminAccessRspData, User } from '@/types';

interface ApiErrorLike {
  message?: string;
  response?: {
    data?: {
      errmsg?: string;
    };
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const apiError = error as ApiErrorLike;
    return apiError.response?.data?.errmsg || apiError.message || fallback;
  }

  return fallback;
}

function getNormalizedUserId(data: { user_id?: string; userId?: string }) {
  return data.user_id || data.userId || '';
}

function getNormalizedIsAdmin(data: { is_admin?: boolean; isAdmin?: boolean }) {
  return Boolean(data.is_admin ?? data.isAdmin);
}

function getNormalizedPermissionsEnabled(data: GetAdminAccessRspData) {
  return Boolean(data.permissions_enabled ?? data.permissionsEnabled);
}

function getNormalizedPermissions(data: GetAdminAccessRspData) {
  return Array.isArray(data.permissions)
    ? data.permissions.filter(
        (permission): permission is string => typeof permission === 'string',
      )
    : [];
}

function isWrappedAdminAccessResponse(response: unknown): response is {
  code: number;
  errmsg?: string;
  data?: GetAdminAccessRspData;
} {
  return (
    typeof response === 'object' && response !== null && 'code' in response
  );
}

class AuthStore {
  user: User | null = null;
  isAuthenticated = false;
  isLoggingIn = false;
  isSendingCode = false;
  isVerifying = false;
  verificationEmail = '';
  initializing = true;
  isAdmin = false;
  permissionsEnabled = false;
  permissions: string[] = [];
  adminAccessChecking = false;
  adminAccessInitialized = false;
  adminAccessError = '';
  private adminAccessCheckedUserId = '';
  private pendingAdminAccessUserId = '';
  private checkAuthPromise: Promise<void> | null = null;
  private checkAdminAccessPromise: Promise<boolean> | null = null;

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => ({
        isAuthenticated: this.isAuthenticated,
        userId: this.user?.user_id ?? '',
      }),
      ({ isAuthenticated, userId }) => {
        if (!isAuthenticated || !userId) {
          return;
        }

        if (
          this.adminAccessChecking ||
          (this.adminAccessInitialized &&
            this.adminAccessCheckedUserId === userId)
        ) {
          return;
        }

        void this.checkAdminAccess();
      },
    );
  }

  private resetAdminAccess() {
    this.isAdmin = false;
    this.permissionsEnabled = false;
    this.permissions = [];
    this.adminAccessChecking = false;
    this.adminAccessInitialized = false;
    this.adminAccessError = '';
    this.adminAccessCheckedUserId = '';
    this.pendingAdminAccessUserId = '';
    this.checkAdminAccessPromise = null;
  }

  can(permission: AdminPermission) {
    return hasAdminPermission(this, permission);
  }

  private async refreshUserAccess() {
    const authenticated = await this.fetchUserInfo();

    if (!authenticated) {
      return false;
    }

    await this.checkAdminAccess();
    return true;
  }

  // 获取用户信息（从 cookie 读取 session_id）
  async fetchUserInfo() {
    try {
      const response = await authApi.getUserInfo();
      const userId = response.data ? getNormalizedUserId(response.data) : '';

      if (response.code === 0 && response.data && userId) {
        runInAction(() => {
          this.user = {
            user_id: userId,
            email: response.data.email,
            avatar: response.data.avatar,
            name: response.data.name,
          };
          this.isAuthenticated = true;
          this.resetAdminAccess();
        });
        return true;
      } else {
        // 请求成功但返回错误码，表示未登录或会话失效
        runInAction(() => {
          this.user = null;
          this.isAuthenticated = false;
          this.resetAdminAccess();
        });
        return false;
      }
    } catch (_error) {
      // 网络错误、401 或其他错误，表示未登录
      // 确保所有失败情况都转为未登录态
      runInAction(() => {
        this.user = null;
        this.isAuthenticated = false;
        this.resetAdminAccess();
      });
      return false;
    }
  }

  async checkAdminAccess(force = false) {
    const currentUserId = this.user?.user_id;

    if (!this.isAuthenticated || !currentUserId) {
      runInAction(() => {
        this.resetAdminAccess();
      });
      return false;
    }

    if (
      !force &&
      this.adminAccessInitialized &&
      this.adminAccessCheckedUserId === currentUserId
    ) {
      return this.isAdmin;
    }

    if (
      this.checkAdminAccessPromise &&
      this.pendingAdminAccessUserId === currentUserId
    ) {
      return this.checkAdminAccessPromise;
    }

    runInAction(() => {
      this.adminAccessChecking = true;
      if (!force) {
        this.adminAccessInitialized = false;
      }
      this.adminAccessError = '';
      this.pendingAdminAccessUserId = currentUserId;
    });

    const requestUserId = currentUserId;
    this.checkAdminAccessPromise = (async () => {
      try {
        const response = await adminAccessApi.getAdminAccess();
        const wrappedResponse = isWrappedAdminAccessResponse(response)
          ? response
          : null;
        const responseData: GetAdminAccessRspData = wrappedResponse?.data
          ? wrappedResponse.data
          : (response as GetAdminAccessRspData);
        const hasAccess = wrappedResponse
          ? wrappedResponse.code === 0 && getNormalizedIsAdmin(responseData)
          : getNormalizedIsAdmin(responseData);
        const responseError =
          wrappedResponse && wrappedResponse.code !== 0
            ? wrappedResponse.errmsg || '权限校验失败，请稍后重试'
            : '';

        runInAction(() => {
          if (this.user?.user_id !== requestUserId) {
            return;
          }

          this.isAdmin = hasAccess;
          this.permissionsEnabled =
            getNormalizedPermissionsEnabled(responseData);
          this.permissions = getNormalizedPermissions(responseData);
          this.adminAccessChecking = false;
          this.adminAccessInitialized = true;
          this.adminAccessError = hasAccess ? '' : responseError;
          this.adminAccessCheckedUserId = requestUserId;
        });

        return hasAccess;
      } catch (error) {
        const errorMsg = getErrorMessage(error, '权限校验失败，请稍后重试');

        runInAction(() => {
          if (this.user?.user_id !== requestUserId) {
            return;
          }

          if (!force) {
            this.isAdmin = false;
            this.permissionsEnabled = false;
            this.permissions = [];
          }
          this.adminAccessChecking = false;
          this.adminAccessInitialized = true;
          this.adminAccessError = errorMsg;
          this.adminAccessCheckedUserId = requestUserId;
        });

        return false;
      } finally {
        runInAction(() => {
          if (this.pendingAdminAccessUserId !== requestUserId) {
            return;
          }

          this.pendingAdminAccessUserId = '';
          this.checkAdminAccessPromise = null;
        });
      }
    })();

    return this.checkAdminAccessPromise;
  }

  // 初始化时检查登录状态（幂等，并发调用只会执行一次）
  async checkAuth() {
    if (this.checkAuthPromise) {
      return this.checkAuthPromise;
    }
    this.initializing = true;
    this.checkAuthPromise = (async () => {
      try {
        await this.refreshUserAccess();
      } finally {
        runInAction(() => {
          this.initializing = false;
          this.checkAuthPromise = null;
        });
      }
    })();
    return this.checkAuthPromise;
  }

  // 邮箱验证码登录
  async loginWithEmail(email: string, code: string) {
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();

    if (trimmedCode.length !== 6) {
      message.error('请输入 6 位验证码');
      return false;
    }

    runInAction(() => {
      this.isVerifying = true;
      this.verificationEmail = trimmedEmail;
    });

    try {
      const response = await authApi.verifyEmailAuthCode(
        trimmedEmail,
        trimmedCode,
        ACCOUNT_APP_ID,
      );
      if (response.code === 0) {
        const success = await this.refreshUserAccess();
        if (success) {
          message.success('登录成功');
          return true;
        }

        message.error('登录成功，但获取用户信息失败');
        return false;
      } else {
        message.error(response.errmsg || '登录失败');
        return false;
      }
    } catch (error) {
      const errorMsg = getErrorMessage(error, '登录失败');
      message.error(errorMsg);
      return false;
    } finally {
      runInAction(() => {
        this.isVerifying = false;
      });
    }
  }

  // Firebase 社交登录
  loginWithOAuth(type: AuthType) {
    runInAction(() => {
      this.isLoggingIn = true;
    });

    void accountService
      .loginWithSocial(type)
      .then(async (callbackURL) => {
        await this.completeOAuthCallback(callbackURL);
      })
      .catch((error: unknown) => {
        const errorMsg = error instanceof Error ? error.message : '登录失败';
        message.error(errorMsg);
      })
      .finally(() => {
        runInAction(() => {
          this.isLoggingIn = false;
        });
      });
  }

  // 发送邮箱验证码
  async sendEmailAuthCode(email: string) {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      message.error('请输入邮箱地址');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      message.error('请输入有效的邮箱地址');
      return false;
    }

    runInAction(() => {
      this.isSendingCode = true;
    });

    try {
      const response = await authApi.sendEmailAuthCode(
        trimmedEmail,
        ACCOUNT_APP_ID,
      );
      if (response.code === 0 && response.data.success) {
        runInAction(() => {
          this.verificationEmail = trimmedEmail;
        });
        message.success('验证码已发送，请查收邮箱');
        return true;
      } else {
        message.error(response.errmsg || '发送验证码失败');
        return false;
      }
    } catch (error) {
      const errorMsg = getErrorMessage(error, '发送验证码失败');
      message.error(errorMsg);
      return false;
    } finally {
      runInAction(() => {
        this.isSendingCode = false;
      });
    }
  }

  async resendEmailAuthCode() {
    const email = this.verificationEmail.trim();
    if (!email) {
      message.error('未找到邮箱地址，请重新输入');
      return false;
    }

    return this.sendEmailAuthCode(email);
  }

  // 登出
  async logout() {
    runInAction(() => {
      this.isLoggingIn = true;
    });

    try {
      await authApi.logout(ACCOUNT_APP_ID);
      runInAction(() => {
        this.user = null;
        this.isAuthenticated = false;
        this.verificationEmail = '';
        this.resetAdminAccess();
      });
      message.success('已登出');
      return true;
    } catch (_error) {
      runInAction(() => {
        this.user = null;
        this.isAuthenticated = false;
        this.verificationEmail = '';
        this.resetAdminAccess();
      });
      message.error('登出失败');
      return false;
    } finally {
      runInAction(() => {
        this.isLoggingIn = false;
      });
    }
  }

  // 设置用户信息
  setUser(user: User | null) {
    this.user = user;
    this.isAuthenticated = user !== null;
    if (!user) {
      this.isLoggingIn = false;
      this.isSendingCode = false;
      this.isVerifying = false;
      this.verificationEmail = '';
      this.resetAdminAccess();
    }
  }

  enableLocalDevBypass(user: User) {
    this.user = user;
    this.isAuthenticated = true;
    this.isLoggingIn = false;
    this.isSendingCode = false;
    this.isVerifying = false;
    this.verificationEmail = '';
    this.initializing = false;
    this.isAdmin = true;
    this.permissionsEnabled = false;
    this.permissions = [];
    this.adminAccessChecking = false;
    this.adminAccessInitialized = true;
    this.adminAccessError = '';
    this.adminAccessCheckedUserId = user.user_id;
    this.pendingAdminAccessUserId = '';
    this.checkAuthPromise = null;
    this.checkAdminAccessPromise = null;
  }

  async completeOAuthCallback(callbackURL: string) {
    runInAction(() => {
      this.isLoggingIn = true;
    });

    try {
      const returnPath =
        await accountService.completeOAuthCallback(callbackURL);
      const success = await this.refreshUserAccess();
      if (!success) {
        throw new Error('登录成功，但获取用户信息失败');
      }
      return returnPath;
    } finally {
      runInAction(() => {
        this.isLoggingIn = false;
      });
    }
  }
}

export const authStore = new AuthStore();
